import { useForm } from '@mantine/form'
import { useEffect, useRef, useState } from 'react'

import arrowRightIcon from '~/widgets/CostBomDemo/assets/images/arrow-right.svg'
import successIcon from '~/widgets/CostBomDemo/assets/images/icon-variant.svg'
import closeIcon from '~/widgets/CostBomDemo/assets/images/x.svg'
import { HeroCountrySelect } from '~/widgets/CostBomDemo/components/HeroCountrySelect'
import {
  isWorkEmail,
  type LeadPayload,
  type LeadResult,
} from '~/widgets/CostBomDemo/hooks/useCostModelLead'
import { COST_BOM_DEMO_STRINGS } from '~/widgets/CostBomDemo/strings'

interface HeroConversionModalProps {
  opened: boolean
  onClose: () => void
  submitLead: (lead: LeadPayload) => LeadResult
}

interface ConversionFormValues {
  product: string
  country: string
  email: string
}

/**
 * The "build your own cost model" conversion form. Fully React-owned:
 * open/close, field state, validation, submission, and the success state all
 * live here (previously this was static markup driven by heroControls.js).
 */
export function HeroConversionModal({
  opened,
  onClose,
  submitLead,
}: HeroConversionModalProps) {
  const productInputRef = useRef<HTMLInputElement>(null)
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)

  const form = useForm<ConversionFormValues>({
    initialValues: { product: '', country: '', email: '' },
    validate: {
      product: (value) => (value.trim() ? null : true),
      country: (value) => (value.trim() ? null : true),
      email: (value) => (isWorkEmail(value) ? null : true),
    },
  })

  // Reset + focus whenever the modal opens. We deliberately do NOT lock the
  // host page's scroll: the widget is embedded, and toggling the page's
  // overflow removes its scrollbar, which shifts/"shrinks" the whole page
  // behind the modal. The fixed backdrop is enough on its own.
  useEffect(() => {
    if (!opened) return () => {}

    form.reset()
    setSubmittedEmail(null)
    const focusTimer = window.setTimeout(
      () => productInputRef.current?.focus(),
      0,
    )

    return () => {
      window.clearTimeout(focusTimer)
    }
    // form identity is stable across renders; opening is the only trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened])

  useEffect(() => {
    if (!opened) return () => {}
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [opened, onClose])

  const handleSubmit = form.onSubmit((values) => {
    const result = submitLead({
      email: values.email.trim(),
      product: values.product.trim(),
      country: values.country.trim(),
      source: 'hero-conversion',
    })

    if (!result.ok) {
      form.setFieldError('email', true)
      return
    }

    setSubmittedEmail(values.email.trim())
  })

  const hasError = Object.keys(form.errors).length > 0
  const isSuccess = submittedEmail !== null

  const successCopy = COST_BOM_DEMO_STRINGS.successDefault

  return (
    <div
      id="hero-conversion-modal"
      className={`hero-conversion-modal${isSuccess ? ' is-success' : ''}`}
      hidden={!opened}
      aria-hidden={!opened}
    >
      <div className="hero-conversion-modal__panel w-form">
        <button
          type="button"
          className="mi-close_icon_button hero-conversion-modal__close"
          aria-label="Close"
          onClick={onClose}
        >
          <img src={closeIcon} alt="" className="image-104" />
        </button>

        <form
          id="wf-form-Cost-Model-Form_step-2"
          className="hero-conversion-modal__form mi-cost_model-email_field-container"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="mi-cost_model-email-text_container">
            <div className="mi-heading6">
              {COST_BOM_DEMO_STRINGS.conversionTitle}
            </div>
            <div className="mi-input">
              <label htmlFor="hero-conv-product" className="mi-heading8-subtle">
                {COST_BOM_DEMO_STRINGS.productName}
              </label>
              <input
                ref={productInputRef}
                className={`mi-cost_model-email_field w-input${
                  form.errors.product ? ' err' : ''
                }`}
                maxLength={256}
                type="text"
                id="hero-conv-product"
                value={form.values.product}
                onChange={(e) =>
                  form.setFieldValue('product', e.currentTarget.value)
                }
              />
            </div>
            <div className="mi-input">
              <label htmlFor="hero-conv-country" className="mi-heading8-subtle">
                {COST_BOM_DEMO_STRINGS.sourcingFromLabel}
              </label>
              <HeroCountrySelect
                value={form.values.country}
                onChange={(value) => form.setFieldValue('country', value)}
                error={!!form.errors.country}
              />
            </div>
            <div className="mi-input">
              <label htmlFor="hero-conv-email" className="mi-heading8-subtle">
                {COST_BOM_DEMO_STRINGS.workEmail}
              </label>
              <input
                className={`mi-cost_model-email_field w-input${
                  form.errors.email ? ' err' : ''
                }`}
                maxLength={256}
                type="email"
                id="hero-conv-email"
                value={form.values.email}
                onChange={(e) =>
                  form.setFieldValue('email', e.currentTarget.value)
                }
              />
            </div>
            <div className={`hero-conv-err${hasError ? ' on' : ''}`}>
              {COST_BOM_DEMO_STRINGS.conversionError}
            </div>
          </div>
          <div className="mi-button_group">
            <input
              type="submit"
              className="mi-button primary-right-icon primary-no-icon w-button"
              value={COST_BOM_DEMO_STRINGS.startDraft}
            />
            <a
              href="https://calendly.com/harris-muir/muir-demo-request"
              target="_blank"
              rel="noopener noreferrer"
              className="mi-button secondary-medium-right-icon w-inline-block"
            >
              <div className="mi-button16">
                {COST_BOM_DEMO_STRINGS.talkToExpert}
              </div>
              <div className="mi-button_icon-container">
                <img src={arrowRightIcon} alt="" className="mi-button_icon" />
              </div>
            </a>
          </div>
        </form>

        <div className="mi-cost_model-success_message w-form-done">
          <div className="mi-cost_model-success_container">
            <div className="mi-cost_model-success_header">
              <img src={successIcon} alt="" className="image-105" />
              <div className="mi-heading6">
                {COST_BOM_DEMO_STRINGS.successTitle}
              </div>
            </div>
            <div className="mi-body4 text-black" id="hero-conv-success-copy">
              {successCopy}
            </div>
            <a
              href="https://calendly.com/harris-muir/muir-demo-request"
              target="_blank"
              rel="noopener noreferrer"
              className="mi-button secondary-medium-right-icon w-inline-block"
            >
              <div className="mi-button16">
                {COST_BOM_DEMO_STRINGS.reviewWithExpert}
              </div>
              <div className="mi-button_icon-container">
                <img src={arrowRightIcon} alt="" className="mi-button_icon" />
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
